import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import "./styles.scss";
import { PromptMediaList } from "../PromptMediaList";
import {
  createVideoThumbnailBlob,
  dataURLtoBlob,
  getDownloadURLFirebase,
  getImageDimensions,
  getValidFiles,
  getVideoDimensions,
  mediaFirebaseUrl,
  saveElementToFirebase,
  saveElementToFirebaseCancellable,
  validAspectRatio,
  validateVideo,
  validDimensions,
} from "@/utils";
import { FileErrorDescription, Media, MediaGroup, PromptType, UploadProgressMap } from "@/interfaces";
import { EnvContext } from "@/contexts/appContexts";
import { Button } from "@/components/ui";

const MAX_MEDIA_REELS = 1;
const MAX_MEDIA = 10;

interface PromptMediaProps {
  values: {
    post?: MediaGroup;
    promptId: number;
    type: PromptType;
  };
  onNewMedia: (newMedia: Media[]) => void;
  onUploading: (uploading: boolean) => void;

  coachId: number;
}

const PromptMediaFC: React.FC<PromptMediaProps> = ({
  values: { post, promptId, type },
  onNewMedia,
  onUploading,
  coachId,
}) => {
  const originalMedia: Media[] = post?.media || [];
  const postAsReel = type == PromptType.REELS;
  const envValues = useContext(EnvContext);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressMap>({});
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [originalIsReel, setOriginalIsReel] = useState<boolean>(postAsReel);
  const [media, setMedia] = useState<Media[]>(originalMedia);
  const maxMediaItems = useMemo(() => {
    return postAsReel ? MAX_MEDIA_REELS : MAX_MEDIA;
  }, [postAsReel]);

  useEffect(() => {
    if (originalIsReel != postAsReel) {
      if (hiddenFileInput.current) {
        hiddenFileInput.current.value = "";
      }
      setMedia([]);
      setOriginalIsReel(postAsReel);
    }
  }, [postAsReel]);

  useEffect(() => {
    onNewMedia(media);
  }, [media]);

  const handleBrowseButtonClicked = () => {
    if (media.length <= maxMediaItems) {
      hiddenFileInput.current?.click();
    } else {
      alert(
        `The maximum number of assets you can add to this prompt is ${maxMediaItems}. Please delete some before adding more.`
      );
    }
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files || new FileList();
    let validFiles = getValidFiles(Array.from(inputFiles));

    // no files provided
    if (validFiles.length === 0) {
      return;
    }

    // too many files? clip the last ones
    const diff = validFiles.length + media.length - maxMediaItems;
    if (diff > 0) {
      alert(`The maximum number of assets you can upload is ${maxMediaItems}. Please delete some before adding more.`);
      const validRange = maxMediaItems - media.length;
      validFiles = validFiles.splice(0, validRange);
    }

    // all file types were valid
    beginFirebaseUpload(validFiles).catch(console.error);
  };

  const beginFirebaseUpload = async (files: File[]) => {
    // get the next available id
    const ids = media.map(item => item.id);
    const minId = ids.length > 0 ? Math.min(...ids) : 0;
    let nextId = minId < 0 ? minId - 1 : -1;
    const sortOrders = media.map(item => item.sortOrder);
    let nextSortOrder = sortOrders.length > 0 ? Math.max(...sortOrders) + 1 : 1;

    const newMedia: Media[] = [];
    const processingPromises: Promise<void>[] = [];
    // pass each new media object to the parent component
    onUploading(true);
    for (const file of files) {
      const mediaCategory = file.type.includes("image/") ? "IMAGE" : "VIDEO";
      const fileErrors = await validateFile(file, mediaCategory);
      const timestamp = new Date().getTime();
      const fileName = file.name.split(".")[0] + timestamp;

      const mediaObject: Media = {
        id: nextId--,
        sortOrder: nextSortOrder++,
        category: mediaCategory,
        uri: "",
        type: file.type,
        extension: file.type.split("/")[1],
        fileName,
        errors: fileErrors,
      };
      newMedia.push(mediaObject);

      const uploadAndProcess = async () => {
        const uploadTask = saveElementToFirebaseCancellable(envValues, mediaFirebaseUrl(`${coachId}`), file, fileName);
        setUploadProgress(prev => ({
          ...prev,
          [mediaObject.id!]: { progress: 0, task: uploadTask },
        }));

        uploadTask.on("state_changed", snapshot => {
          handleFirebaseUploadProgress(snapshot, mediaObject.id!);
        });

        await uploadTask;
        const finalMedia = await handleFirebaseUploadComplete(file, mediaObject);
        handleUpdateMediaObject(finalMedia);
      };

      processingPromises.push(uploadAndProcess());
    }

    setMedia(currentMedia => [...currentMedia, ...newMedia]);
    await Promise.all(processingPromises);
    onUploading(false);
  };

  const validateFile = async (file: File, category: "IMAGE" | "VIDEO"): Promise<FileErrorDescription | undefined> => {
    if (category == "VIDEO") {
      return await validateVideo(file);
    } else {
      return {
        wrongBitrate: false,
        wrongSize: false,
        wrongLength: false,
      };
    }
  };

  const uploadThumbnailToFirebase = async (
    uri: string,
    mediaObject: Media,
    isValidAspectRatio: boolean,
    isValidDimensions: boolean
  ): Promise<Media> => {
    try {
      const videoThumbnailUri = await createVideoThumbnailBlob(uri);
      const blob = dataURLtoBlob(videoThumbnailUri);
      const thumbUrl = await saveElementToFirebase(
        envValues,
        "thumbnail/" + mediaObject.fileName,
        blob,
        "thumbnail_" + mediaObject.fileName
      );

      return {
        ...mediaObject,
        uri,
        downloadLink: uri,
        thumbnailUri: thumbUrl,
        validAspectRatio: isValidAspectRatio,
        validDimension: isValidDimensions,
      };
    } catch (e) {
      console.error(e);
      return {
        ...mediaObject,
        uri,
        downloadLink: uri,
        validAspectRatio: isValidAspectRatio,
        validDimension: isValidDimensions,
      };
    }
  };

  const handleFirebaseUploadProgress = (snapshot: any, mediaId: number) => {
    const progress = snapshot.bytesTransferred / snapshot.totalBytes;
    setUploadProgress(map => {
      return {
        ...map,
        [mediaId]: {
          ...map[mediaId],
          progress,
        },
      };
    });
  };

  const handleFirebaseUploadComplete = async (file: File, mediaObject: Media): Promise<Media> => {
    const uri = await getDownloadURLFirebase(envValues, mediaFirebaseUrl(`${coachId}`), file, mediaObject.fileName!);

    if (mediaObject.category === "VIDEO") {
      const dimension = await getVideoDimensions(uri);
      const finalMedia = await uploadThumbnailToFirebase(
        uri,
        mediaObject,
        dimension ? validAspectRatio(dimension.height, dimension.width, postAsReel) : true,
        dimension ? validDimensions(dimension.height, dimension.width, postAsReel) : true
      );
      return finalMedia;
    } else {
      const dimension = await getImageDimensions(uri);
      return {
        ...mediaObject,
        uri,
        downloadLink: uri,
        validAspectRatio: dimension ? validAspectRatio(dimension.height, dimension.width) : true,
        validDimension: true,
        imageFile: file,
      };
    }
  };

  const handleUpdateMediaObject = (mediaObject: Media) => {
    setMedia(current =>
      current.map(m => {
        if (m.id === mediaObject.id) {
          return mediaObject;
        }
        return m;
      })
    );
  };

  const handleDeletedMedia = (id: number) => {
    // update state
    uploadProgress[id]?.task?.cancel();
    setMedia(current => current.filter(mediaItem => mediaItem.id !== id));
  };

  const updateMediaUri = (id: number, uri: string) => {
    setMedia(current =>
      current.map(mediaItem => {
        if (mediaItem.id === id) {
          return {
            ...mediaItem,
            uri,
            validAspectRatio: true,
            downloadLink: uri,
          };
        }

        return mediaItem;
      })
    );
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    setMedia(current => {
      const result = [...current];
      const [removed] = result.splice(source.index, 1);
      result.splice(destination.index, 0, removed);

      return result.map((m: Media, index) => ({ ...m, sortOrder: index })) as Media[];
    });
  };

  return (
    <div className="prompt-media-wrapper">
      <form className="prompt-media-input-form">
        <input
          className="custom-file-input"
          type="file"
          multiple={!postAsReel}
          accept={postAsReel ? "video/*" : "image/*, video/*"}
          onChange={handleFilesSelected}
          ref={hiddenFileInput}
        />
        {media.length > 0 && (
          <div className={"uploaded-files-manager"}>
            <PromptMediaList
              promptId={promptId}
              media={media}
              isForReel={postAsReel}
              uploadProgress={uploadProgress}
              handleClose={handleDeletedMedia}
              handleUpdate={updateMediaUri}
              onDragEnd={onDragEnd}
            />
          </div>
        )}
        {media.length < maxMediaItems && <Button onClick={handleBrowseButtonClicked}>Add File</Button>}
      </form>
    </div>
  );
};

export const PromptMedia = PromptMediaFC;
